import { useRef, useState, useCallback } from 'react';
import { Upload, X, ImageIcon, Loader2, GripVertical } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { cn } from '@/lib/utils';

interface ImagePreview {
  src: string;
  isNew?: boolean;
  id?: number | string;
  label?: string;
}

interface FormImageUploadProps {
  previews: ImagePreview[];
  maxImages?: number;
  onAdd: (files: FileList) => void;
  onRemove: (index: number, id?: number | string) => void;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  isCompressing?: boolean;
  mainLabel?: string;
  secondaryLabel?: string;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  addMoreLabel?: string;
  newBadgeLabel?: string;
  reorderHint?: string;
}

export function FormImageUpload({
  previews,
  maxImages = 5,
  onAdd,
  onRemove,
  onReorder,
  isCompressing = false,
  mainLabel,
  secondaryLabel,
  emptyStateTitle = 'Click to upload',
  emptyStateDescription = 'Up to 5 images',
  addMoreLabel = 'Add more',
  newBadgeLabel = 'New',
  reorderHint = 'Drag to reorder',
}: FormImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onAdd(files);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const canAddMore = previews.length < maxImages;

  // Drag and drop handlers
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    if (!onReorder) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    // Add a slight delay to allow the drag image to be captured
    setTimeout(() => {
      const element = e.target as HTMLElement;
      element.style.opacity = '0.5';
    }, 0);
  }, [onReorder]);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    const element = e.target as HTMLElement;
    element.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (!onReorder) return;
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  }, [onReorder, draggedIndex]);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    if (!onReorder) return;

    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (!isNaN(fromIndex) && fromIndex !== toIndex) {
      onReorder(fromIndex, toIndex);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [onReorder]);

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {isCompressing && (
        <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          <span className="text-sm">Compressing images...</span>
        </div>
      )}

      {previews.length > 0 ? (
        <div className="space-y-2">
          {onReorder && previews.length > 1 && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <GripVertical className="size-3" />
              {reorderHint}
            </p>
          )}
          <div className="grid grid-cols-2 gap-3 flex-wrap">
            {previews.map((preview, index) => (
              <div
                key={preview.id ?? `preview-${index}`}
                draggable={!!onReorder}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                className={cn(
                  'group relative aspect-square rounded-lg border bg-muted overflow-hidden transition-all',
                  onReorder && 'cursor-grab active:cursor-grabbing',
                  draggedIndex === index && 'opacity-50 scale-95',
                  dragOverIndex === index && 'ring-2 ring-primary ring-offset-2',
                )}
              >
                <img
                  src={preview.src}
                  alt={`Preview ${index + 1}`}
                  className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                />

                {/* Drag handle indicator */}
                {onReorder && (
                  <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 rounded p-1">
                    <GripVertical className="size-4 text-white" />
                  </div>
                )}

                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 size-7 opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={() => onRemove(index, preview.id)}
                >
                  <X className="size-4" />
                </Button>

                {/* Position indicator */}
                <span className="absolute bottom-2 right-2 text-xs bg-black/60 text-white px-2 py-0.5 rounded">
                  {index + 1}
                </span>

                {index === 0 && mainLabel && (
                  <span className="absolute bottom-2 left-2 text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
                    {mainLabel}
                  </span>
                )}
                {index === 1 && secondaryLabel && (
                  <span className="absolute bottom-2 left-2 text-xs bg-secondary text-secondary-foreground px-2 py-1 rounded">
                    {secondaryLabel}
                  </span>
                )}
                {preview.isNew && (
                  <span className="absolute bottom-2 left-2 text-xs bg-green-600 text-white px-2 py-1 rounded">
                    {newBadgeLabel}
                  </span>
                )}
                {preview.label && !preview.isNew && index > 0 && (
                  <span className="absolute bottom-2 left-2 text-xs bg-muted text-muted-foreground px-2 py-1 rounded">
                    {preview.label}
                  </span>
                )}
              </div>
            ))}

            {canAddMore && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isCompressing}
                className="flex aspect-square flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/50 text-muted-foreground transition-colors hover:border-muted-foreground/50 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCompressing ? (
                  <Loader2 className="size-6 animate-spin" />
                ) : (
                  <Upload className="size-6" />
                )}
                <span className="text-xs">{addMoreLabel}</span>
              </button>
            )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isCompressing}
          className="flex w-full flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/50 py-12 text-muted-foreground transition-colors hover:border-muted-foreground/50 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="rounded-full bg-muted p-4">
            {isCompressing ? (
              <Loader2 className="size-8 animate-spin" />
            ) : (
              <ImageIcon className="size-8" />
            )}
          </div>
          <div className="text-center">
            <p className="font-medium">{emptyStateTitle}</p>
            <p className="text-sm">{emptyStateDescription}</p>
          </div>
        </button>
      )}
    </div>
  );
}
